import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
    Search, Plus, Edit, Trash2, Eye, Car, Bike, CheckCircle, XCircle,
    Star, MapPin, Loader2
} from 'lucide-react'
import { useLanguageStore } from '../../lib/store'
import { formatCurrency } from '../../lib/utils'
import { vehiclesApi, adminApi } from '../../lib/api'

interface Vehicle {
    id: number
    type: string
    brand: string
    model: string
    daily_rate: number
    location_address: string
    status: string
    owner_name: string
    owner_id: number
    avg_rating: number
    review_count: number
    booking_count: number
    created_at: string
}

interface Stats {
    total: number
    active: number
    pending: number
    inactive: number
    motorcycles: number
    cars: number
}

export default function VehicleManagement() {
    useTranslation()
    const { language } = useLanguageStore()

    const [searchQuery, setSearchQuery] = useState('')
    const [statusFilter, setStatusFilter] = useState('all')
    const [typeFilter, setTypeFilter] = useState('all')
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null)
    const [vehicles, setVehicles] = useState<Vehicle[]>([])
    const [stats, setStats] = useState<Stats>({ total: 0, active: 0, pending: 0, inactive: 0, motorcycles: 0, cars: 0 })
    const [loading, setLoading] = useState(true)
    const [deleting, setDeleting] = useState(false)

    const statuses = [
        { id: 'all', name: { en: 'All', vi: 'Tất cả' } },
        { id: 'active', name: { en: 'Active', vi: 'Hoạt động' } },
        { id: 'pending', name: { en: 'Pending', vi: 'Chờ duyệt' } },
        { id: 'inactive', name: { en: 'Inactive', vi: 'Tạm dừng' } },
    ]

    const fetchVehicles = async () => {
        setLoading(true)
        try {
            const params: any = { search: searchQuery }
            if (statusFilter !== 'all') params.status = statusFilter
            if (typeFilter !== 'all') params.type = typeFilter

            const response = await vehiclesApi.adminList(params)
            if (response.data?.data) {
                setVehicles(response.data.data)
            }
            if (response.data?.stats) {
                setStats(response.data.stats)
            }
        } catch (error) {
            console.error('Failed to fetch vehicles:', error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchVehicles()
    }, [statusFilter, typeFilter, searchQuery])

    const handleApprove = async (id: number) => {
        try {
            await adminApi.approveVehicle(id)
            setVehicles(prev => prev.map(v => v.id === id ? { ...v, status: 'active' } : v))
        } catch (error) {
            console.error('Failed to approve:', error)
        }
    }

    const handleReject = async (id: number) => {
        try {
            await adminApi.rejectVehicle(id)
            setVehicles(prev => prev.map(v => v.id === id ? { ...v, status: 'inactive' } : v))
        } catch (error) {
            console.error('Failed to reject:', error)
        }
    }

    const handleDelete = async () => {
        if (!selectedVehicle) return
        setDeleting(true)
        try {
            await vehiclesApi.adminDelete(selectedVehicle.id)
            setShowDeleteModal(false)
            setSelectedVehicle(null)
            fetchVehicles()
        } catch (error) {
            console.error('Failed to delete:', error)
        } finally {
            setDeleting(false)
        }
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {language === 'en' ? 'Vehicle Management' : 'Quản Lý Xe'}
                    </h1>
                    <p className="text-gray-500 mt-1">
                        {language === 'en' ? 'Manage all rental vehicles' : 'Quản lý tất cả xe cho thuê'}
                    </p>
                </div>
                <Link
                    to="/admin/vehicles/new"
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition"
                >
                    <Plus className="w-5 h-5" />
                    {language === 'en' ? 'Add Vehicle' : 'Thêm xe'}
                </Link>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Total' : 'Tổng cộng'}</p>
                    <p className="text-2xl font-bold mt-1">{stats.total}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Active' : 'Hoạt động'}</p>
                    <p className="text-2xl font-bold mt-1 text-green-600">{stats.active}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Pending' : 'Chờ duyệt'}</p>
                    <p className="text-2xl font-bold mt-1 text-orange-600">{stats.pending}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Motorcycles' : 'Xe máy'}</p>
                    <p className="text-2xl font-bold mt-1 text-blue-600">{stats.motorcycles}</p>
                </div>
                <div className="bg-white rounded-xl p-4 border">
                    <p className="text-gray-500 text-sm">{language === 'en' ? 'Cars' : 'Ô tô'}</p>
                    <p className="text-2xl font-bold mt-1 text-purple-600">{stats.cars}</p>
                </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 border">
                <div className="flex flex-col md:flex-row gap-4">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="search"
                            placeholder={language === 'en' ? 'Search vehicles or owners...' : 'Tìm xe hoặc chủ xe...'}
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500"
                        />
                    </div>
                    <div className="flex gap-2 flex-wrap">
                        {statuses.map((status) => (
                            <button
                                key={status.id}
                                onClick={() => setStatusFilter(status.id)}
                                className={`px-4 py-2 rounded-lg text-sm font-medium transition ${statusFilter === status.id
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                {status.name[language]}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => setTypeFilter('all')}
                            className={`p-2 rounded-lg ${typeFilter === 'all' ? 'bg-primary-100 text-primary-600' : 'bg-gray-100'}`}
                        >
                            All
                        </button>
                        <button
                            onClick={() => setTypeFilter('motorcycle')}
                            className={`p-2 rounded-lg ${typeFilter === 'motorcycle' ? 'bg-primary-100 text-primary-600' : 'bg-gray-100'}`}
                        >
                            <Bike className="w-5 h-5" />
                        </button>
                        <button
                            onClick={() => setTypeFilter('car')}
                            className={`p-2 rounded-lg ${typeFilter === 'car' ? 'bg-primary-100 text-primary-600' : 'bg-gray-100'}`}
                        >
                            <Car className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Vehicles Table */}
            {loading ? (
                <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            ) : (
                <div className="bg-white rounded-xl border overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[800px]">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                        {language === 'en' ? 'Vehicle' : 'Xe'}
                                    </th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase hidden md:table-cell">
                                        {language === 'en' ? 'Owner' : 'Chủ xe'}
                                    </th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">
                                        {language === 'en' ? 'Price' : 'Giá'}
                                    </th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase hidden lg:table-cell">
                                        {language === 'en' ? 'Rating' : 'Đánh giá'}
                                    </th>
                                    <th className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                        {language === 'en' ? 'Status' : 'Trạng thái'}
                                    </th>
                                    <th className="text-right px-6 py-3 text-xs font-medium text-gray-500 uppercase">
                                        {language === 'en' ? 'Actions' : 'Thao tác'}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {vehicles.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="text-center py-12 text-gray-500">
                                            {language === 'en' ? 'No vehicles found' : 'Không tìm thấy xe'}
                                        </td>
                                    </tr>
                                ) : (
                                    vehicles.map((vehicle) => (
                                        <tr key={vehicle.id} className="hover:bg-gray-50">
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                                        {vehicle.type === 'motorcycle' ? (
                                                            <Bike className="w-6 h-6 text-gray-400" />
                                                        ) : (
                                                            <Car className="w-6 h-6 text-gray-400" />
                                                        )}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{vehicle.brand} {vehicle.model}</p>
                                                        <p className="text-sm text-gray-500 flex items-center gap-1">
                                                            <MapPin className="w-3 h-3" />
                                                            {vehicle.location_address || 'Dương Đông'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 hidden md:table-cell">
                                                <span className="text-gray-700">{vehicle.owner_name || 'Unknown'}</span>
                                            </td>
                                            <td className="px-6 py-4 hidden lg:table-cell">
                                                <span className="font-medium text-green-600">
                                                    {formatCurrency(vehicle.daily_rate, 'VND')}
                                                </span>
                                                <span className="text-gray-500 text-sm">/{language === 'en' ? 'day' : 'ngày'}</span>
                                            </td>
                                            <td className="px-6 py-4 hidden lg:table-cell">
                                                {(vehicle.avg_rating || 0) > 0 ? (
                                                    <div className="flex items-center gap-1">
                                                        <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                                                        <span className="font-medium">{vehicle.avg_rating?.toFixed(1)}</span>
                                                        <span className="text-gray-500 text-sm">({vehicle.review_count || 0})</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-gray-400">{language === 'en' ? 'No reviews' : 'Chưa có'}</span>
                                                )}
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2 py-1 rounded text-xs font-medium ${vehicle.status === 'active' ? 'bg-green-100 text-green-700' :
                                                    vehicle.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                                                        'bg-gray-100 text-gray-700'
                                                    }`}>
                                                    {vehicle.status === 'active' && (language === 'en' ? 'Active' : 'Hoạt động')}
                                                    {vehicle.status === 'pending' && (language === 'en' ? 'Pending' : 'Chờ duyệt')}
                                                    {vehicle.status === 'inactive' && (language === 'en' ? 'Inactive' : 'Tạm dừng')}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Link
                                                        to={`/vehicles/${vehicle.id}`}
                                                        className="p-2 hover:bg-gray-100 rounded-lg"
                                                        title={language === 'en' ? 'View' : 'Xem'}
                                                    >
                                                        <Eye className="w-4 h-4 text-gray-600" />
                                                    </Link>
                                                    <Link
                                                        to={`/admin/vehicles/${vehicle.id}/edit`}
                                                        className="p-2 hover:bg-blue-50 rounded-lg"
                                                        title={language === 'en' ? 'Edit' : 'Sửa'}
                                                    >
                                                        <Edit className="w-4 h-4 text-blue-600" />
                                                    </Link>
                                                    {vehicle.status === 'pending' && (
                                                        <>
                                                            <button
                                                                onClick={() => handleApprove(vehicle.id)}
                                                                className="p-2 hover:bg-green-50 rounded-lg"
                                                                title={language === 'en' ? 'Approve' : 'Duyệt'}
                                                            >
                                                                <CheckCircle className="w-4 h-4 text-green-600" />
                                                            </button>
                                                            <button
                                                                onClick={() => handleReject(vehicle.id)}
                                                                className="p-2 hover:bg-red-50 rounded-lg"
                                                                title={language === 'en' ? 'Reject' : 'Từ chối'}
                                                            >
                                                                <XCircle className="w-4 h-4 text-red-600" />
                                                            </button>
                                                        </>
                                                    )}
                                                    <button
                                                        onClick={() => {
                                                            setSelectedVehicle(vehicle)
                                                            setShowDeleteModal(true)
                                                        }}
                                                        className="p-2 hover:bg-red-50 rounded-lg"
                                                        title={language === 'en' ? 'Delete' : 'Xóa'}
                                                    >
                                                        <Trash2 className="w-4 h-4 text-red-600" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl p-6 max-w-md w-full">
                        <h3 className="text-lg font-bold mb-2">
                            {language === 'en' ? 'Delete Vehicle' : 'Xóa Xe'}
                        </h3>
                        <p className="text-gray-600 mb-4">
                            {language === 'en'
                                ? `Are you sure you want to delete "${selectedVehicle?.brand} ${selectedVehicle?.model}"?`
                                : `Bạn có chắc muốn xóa "${selectedVehicle?.brand} ${selectedVehicle?.model}"?`
                            }
                        </p>
                        <div className="flex gap-3">
                            <button
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleting}
                                className="flex-1 py-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                            >
                                {language === 'en' ? 'Cancel' : 'Hủy'}
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={deleting}
                                className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {deleting && <Loader2 className="w-4 h-4 animate-spin" />}
                                {language === 'en' ? 'Delete' : 'Xóa'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
